import React, { useEffect, useState, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { toast } from 'react-toastify';

import styles from './Milestones.module.scss';
import WarToken from '../../assets/images/war_token.png';
import Arrow from '../../assets/images/arrow_yellow.png';

import PopupInfo from '../../components/PopupInfo/PopupInfo';
import Loading from '../../components/Loading/Loading';
import {ENV, ERROR_MAINTENANCE, MOBILE_VIEW_BREAKPOINT, TOASTS_TYPES} from '../../shared/constants';
import { request } from '../../shared/request';
import { withResponseHandling, performLogout } from '../../shared/responseHandler';
import MilestoneCard from '../../components/MilestoneCard/MilestoneCard';
import { verifyMilestoneHashAndGetId, hasMilestonesToClaimLeft } from '../../shared/helpers';

/** Maximum number of toast notifications allowed on screen simultaneously */
const MAX_TOASTS_ON_SCREEN = 3;
/** Duration in milliseconds for which a toast notification remains visible */
const TOAST_SCREENTIME = 5000;

/**
 * Milestones page component that displays and manages player milestones
 * 
 * @param {Object} props - Component props
 * @param {Object} props.config - Configuration object containing localized text and settings
 * @param {Object} props.windowSize - Window size object with viewport information
 * @param {boolean} props.windowSize.isMobileView - Indicates if the current view is mobile
 * @param {Function} props.setIsLogged - Function to update the logged-in state
 * @param {Function} props.setHasUnclaimedMilestones - Function to update unclaimed milestones state
 * @returns {JSX.Element} The Milestones page component
 */
export default function Milestones({ config, windowSize, setIsLogged, setHasUnclaimedMilestones }) {
    const navigate = useNavigate();
    const { hash } = useLocation();
    
    /** Ref to track currently displayed toast notification IDs */
    const shownToasts = useRef([]);
    /** Ref to the milestones wrapper element for scroll management */
    const milestonesWrapper = useRef();
    
    /** State containing all milestone data fetched from the API */
    const [allMilestones, setAllMilestones] = useState([]);
    /** Loading state indicator for async operations */
    const [isLoading, setIsLoading] = useState(false);
    /** Error state object containing error status and message */
    const [error, setError] = useState({});
    /** Flag indicating whether the player has no milestones available */
    const [hasEmptyMilestones, setHasEmptyMilestones] = useState(false);

    /**
     * Logs out the current user and performs necessary cleanup
     * Clears authentication state and navigates to login page
     */
    /**
     * Logs out the current user and performs necessary cleanup
     * Clears authentication state and navigates to login page
     */
    const logOut = () => {
        performLogout({
            navigate,
            setIsLogged,
            setHasUnclaimedMilestones,
        });
    };

    /**
     * Fetches all milestones data for the current player
     * Retrieves player info from localStorage and makes API call to get milestone data
     * Updates state with fetched milestones or sets empty state if none exist
     * Handles errors and access denied scenarios appropriately
     */
    const getMilestones = async () => {
        let player = localStorage.getItem('playerInfo');
        player = JSON.parse(player);

        try {
            setIsLoading(true);
            
            await withResponseHandling(
                () => request.get(`getWebShopInfo/${player.id}`),
                {
                    navigate,
                    onSuccess: (response) => {
                        if (response.Milestones && response.Milestones.length) {
                            setAllMilestones(response.Milestones);
                            setHasEmptyMilestones(false);
                        } else {
                            setAllMilestones([]);
                            setHasEmptyMilestones(true);
                        }
                    },
                    onAccessDenied: () => {
                        logOut();
                    },
                    onEmpty: () => {
                        logOut();
                    },
                    onError: (err) => {
                        setError({
                            hasError: true,
                            message: ENV === 'test' ? err.message : null,
                        });
                    },
                }
            );
        } catch (err) {
            setError({
                hasError: true,
                message: ENV === 'test' ? err.message : null,
            });
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Claims a milestone reward for the player
     * 
     * @param {number} milestoneId - The ID of the milestone to claim
     * @param {number} progressInPercent - The progress percentage of the milestone (0-100)
     * @returns {Promise<void>}
     * 
     * Only processes if milestone is 100% complete
     * Automatically navigates to next claimable milestone if available
     * Updates milestone claimed status on success
     */
    const claimReward = async (milestoneId, progressInPercent) => {
        // Only allow claiming if milestone is 100% complete
        if (progressInPercent < 100) {
            return;
        }

        // Ignore repeat clicks on a milestone that is already claimed.
        const target = allMilestones.find((milestone) => milestone.Id === milestoneId);
        if (!target || target.Claimed) {
            return;
        }

        // Check if there's a next milestone that's ready to claim and navigate to it
        const nextMilestone = allMilestones.find((milestone) => milestone.Id === milestoneId + 1);
        if (nextMilestone && !nextMilestone.Claimed && (nextMilestone.CollectedWarTokens >= nextMilestone.RequiredTokens)) {
            navigate(`#milestone${milestoneId + 1}`);
        } else {
            navigate(`#milestone${milestoneId}`);
        }

        let player = localStorage.getItem('playerInfo');
        player = JSON.parse(player);

        const milestoneIndex = allMilestones.findIndex((milestone) => milestone.Id === milestoneId);

        try {
            setIsLoading(true);
            
            await withResponseHandling(
                () => request.post(`claimMilestoneReward/${player.id}/${milestoneId}`),
                {
                    navigate,
                    // This endpoint returns a bare {Success: bool} with no
                    // CanShop flag, so it must not be gated on shop access -
                    // otherwise onSuccess never fires and the card silently
                    // stays on "claim" even though the claim succeeded.
                    requiresShopAccess: false,
                    onSuccess: (response) => {
                        if (response.Success) {
                            toast.success(config.milestoneClaimSuccess);
                            // Replace the milestone immutably so the card
                            // re-renders as "claimed" straight away.
                            const updatedMilestones = allMilestones.map((milestone) => (
                                milestone.Id === milestoneId
                                    ? { ...milestone, Claimed: true }
                                    : milestone
                            ));
                            setAllMilestones(updatedMilestones);

                            // Check if there are any more milestones left to claim
                            if (!hasMilestonesToClaimLeft(updatedMilestones)) {
                                setHasUnclaimedMilestones(false);
                            }
                        } else {
                            toast.error(config.milestoneClaimFail);
                        }
                    },
                    onEmpty: () => {
                        toast.error(config.milestoneClaimFail);
                    },
                    onError: (err) => {
                        toast.error(config.milestoneClaimFail);
                        setError({
                            hasError: true,
                            message: ENV === 'test' ? err.message : null,
                        });
                    },
                }
            );
        } catch (err) {
            toast.error(config.milestoneClaimFail);
            setError({
                hasError: true,
                message: ENV === 'test' ? err.message : null,
            });
        } finally {
            setIsLoading(false);
        }
    };

    /**
     * Manages toast notification queue to prevent screen clutter
     * Limits the number of simultaneous toasts to MAX_TOASTS_ON_SCREEN
     * Automatically dismisses oldest toast when limit is reached
     * 
     * @param {string} type - Type of toast notification (success or error)
     */
    const toastsDeboucer = (type) => {
        if (shownToasts.current.length >= MAX_TOASTS_ON_SCREEN) {
            // Remove oldest toast to make room for new one
            toast.dismiss(shownToasts.current[0]);
            shownToasts.current.shift();
            let currentToastId = null;

            if (type === TOASTS_TYPES.success) {
                currentToastId = toast.success(config.milestoneClaimSuccess);
            } else {
                currentToastId = toast.error(config.milestoneClaimFail);
            }

            shownToasts.current.push(currentToastId);
            removeToastId(currentToastId);
        } else {
            let currentToastId = null;

            if (type === TOASTS_TYPES.success) {
                currentToastId = toast.success(config.milestoneClaimSuccess);
            } else {
                currentToastId = toast.error(config.milestoneClaimFail);
            }

            shownToasts.current.push(currentToastId);
            removeToastId(currentToastId);
        }
    };

    /**
     * Removes a toast ID from the tracking array after its display duration
     * Used for cleanup of expired toast notifications
     * 
     * @param {string|number} id - The toast notification ID to remove
     */
    const removeToastId = (id) => {
        setTimeout(() => {
            shownToasts.current && shownToasts.current.length > 0 && shownToasts.current.splice(id, 1);
        }, TOAST_SCREENTIME);
    };

    /**
     * Transforms vertical scroll (wheel) events into horizontal scroll
     * Only applies on desktop views (width > MOBILE_VIEW_BREAKPOINT)
     * Prevents default vertical scrolling behavior
     * 
     * @param {WheelEvent} event - The wheel event from user interaction
     */
    const  transformScroll = (event) => {
        if (window.innerWidth > MOBILE_VIEW_BREAKPOINT) {
            event.preventDefault();

            if (!event.deltaY) {
                return;
            }

            // Convert vertical scroll to horizontal scroll
            event.currentTarget.scrollLeft += event.deltaY + event.deltaX;
        }
    }

    /**
     * Smoothly scrolls to a specific milestone and updates the URL hash
     * 
     * @param {string} milestoneId - The DOM ID of the milestone element to scroll to
     */
    const goToMilestone = (milestoneId) => {
        document.getElementById(milestoneId).scrollIntoView({behavior: "smooth"});
        navigate(`#${milestoneId}`);
    }

    /**
     * Effect: Validates player authentication on component mount
     * Logs out the user if player info is missing or invalid
     */
    useEffect(() => {
        let player = localStorage.getItem('playerInfo');
        player = JSON.parse(player);

        if (!player || !player.id) {
            logOut();
        }
    }, []);

    /**
     * Effect: Fetches milestones data on component mount
     */
    useEffect(() => {
        getMilestones();
    }, []);

    /**
     * Effect: Attaches horizontal scroll transform listener when loading completes
     * Enables mouse wheel to control horizontal scrolling on desktop
     */
    useEffect(() => {
        if (!isLoading) {
            milestonesWrapper.current.addEventListener('wheel', transformScroll);
        }
    }, [isLoading]);

    /**
     * Effect: Handles automatic scrolling to milestones based on URL hash or progress
     * 
     * Behavior:
     * 1. If URL hash exists, scrolls to the specified milestone
     * 2. If no hash, scrolls to the first claimable milestone (collected >= required && not claimed)
     * 3. If no claimable milestones, scrolls to the next unclaimed milestone after the last claimed one
     * 
     * Runs when hash changes, loading completes, or milestones data updates
     */
    useEffect(() => {
        let selectedMilestone = '';

        // Handle URL hash navigation to specific milestone
        if (hash && !isLoading && (allMilestones && allMilestones.length)) {
            selectedMilestone = verifyMilestoneHashAndGetId(hash);
            if (selectedMilestone) {
                document.getElementById(selectedMilestone).scrollIntoView({ behavior: "smooth" });
            }
        }

        // Auto-scroll to relevant milestone when no hash is present
        if (!hash && !isLoading && (allMilestones && allMilestones.length)) {
            // First, try to find a milestone that's ready to claim
            const milestoneToScroll = allMilestones.find((milestone) => {
                return !milestone.Claimed && (milestone.CollectedWarTokens >= milestone.RequiredTokens)
            });

            if (milestoneToScroll && milestoneToScroll.Id) {
                document.getElementById(`milestone${milestoneToScroll.Id}`).scrollIntoView({behavior: "smooth"});
            } else {
                // If no claimable milestone, scroll to the next unclaimed one
                const lastClaimedMilestone = allMilestones.findLastIndex((milestone) => milestone.Claimed);
                if ((lastClaimedMilestone !== -1) && (lastClaimedMilestone <= allMilestones.length - 1)) {
                    document.getElementById(`milestone${lastClaimedMilestone + 1}`).scrollIntoView({behavior: "smooth"});
                }
            }
        }
    }, [hash, isLoading]);

    // Render loading spinner while fetching data
    return isLoading ? (
        <Loading />
    ) : (
        <div className={styles.wrapper}>
            {/* Page title */}
            <div className={styles.milestonesTitle}>
                {config.milestones}
            </div>
            {/* Instructional description about war tokens and milestones */}
            <div className={styles.milestonesDescription}>
                {config.getWarTokens}
                <span className={styles.imgPart}>&nbsp;(<img className={styles.warTokenImg} src={WarToken} alt='' />)&nbsp;</span>
                {config.warTokensDesc}
            </div>
            {/* Scrollable container for milestone cards */}
            <div className={styles.milestonesWrapper} ref={milestonesWrapper}>
                {allMilestones && allMilestones.length > 0 && allMilestones.map((milestone, index) => (
                    <div key={index} id={`milestone${index + 1}`} className={`${styles.milestoneCardWrapper} ${!windowSize.isMobileView ? styles.horizontal : ''}`}>
                        <MilestoneCard config={config} {...milestone} claimReward={claimReward} />
                        {/* Show navigation arrow between milestones, except after the last one */}
                        {index !== (allMilestones.length - 1) ?
                            <img
                                className={styles.milestoneArrow}
                                src={Arrow}
                                alt={''}
                                onClick={() => goToMilestone(`milestone${index + 2}`)}
                            />
                        :
                            null
                        }
                    </div>
                ))}
            </div>
            {/* Display message when player has no milestones */}
            {hasEmptyMilestones ?
                <div className={styles.noMilestonesWrapper}>
                    {config.noMilestones}
                </div>
            :
                null
            }
            {/* Error popup displayed when an error occurs */}
            <AnimatePresence>
                {error.hasError ?
                    <PopupInfo text={error.message} setError={setError} />
                    :
                    null
                }
            </AnimatePresence>
        </div>    
    );
};
